import {
  BarChart,
  Bar,
  Brush,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  Tooltip,
  LabelList,
  ResponsiveContainer,
} from "recharts";

import { useMediaQuery } from "react-responsive";
import { ChartWrapper } from "../../components/ChartWrapper";
import { formatNumber } from "../../../utils/number";
import { formatLabel } from "../../../utils/string";

/* -------- SMART LABEL -------- */
const renderSmartLabel = (data, key, isSmall) => (props) => {
  const { x, y, width, height, index } = props;
  const value = data?.[index]?.[key];

  if (!value) return null;

  // small bar → show above
  if (height < 18) {
    return (
      <text
        x={x + width / 2}
        y={y - 4}
        fill="#111"
        textAnchor="middle"
        fontSize={isSmall ? 9 : 11}
        fontWeight="600"
      >
        {formatNumber(value)}
      </text>
    );
  }

  // normal → inside
  return (
    <text
      x={x + width / 2}
      y={y + height / 2}
      fill="#fff"
      textAnchor="middle"
      dominantBaseline="middle"
      fontSize={isSmall ? 9 : 11}
      fontWeight="700"
    >
      {formatNumber(value)}
    </text>
  );
};

export default function LearningStrength({ data = [], loading = false }) {
  const isSmall = useMediaQuery({ query: "(max-width: 600px)" });

  const isEmpty = !data || data.length === 0;

  return (
    <ChartWrapper
      loading={loading}
      isEmpty={isEmpty}
      title="Learning Strength & Gap"
      description="Comparison of strong vs weak learning areas"
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          barCategoryGap={isSmall ? "15%" : "20%"}
          margin={{
            top: 30,
            right: 20,
            left: isSmall ? 0 : 10,
            bottom: isSmall ? 40 : 60,
          }}
        >
          {/* GRID */}
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />

          {/* X AXIS */}
          <XAxis
            dataKey="Subject"
            tickFormatter={(v) => formatLabel(v)}
            interval={0}
            tick={{
              fontSize: isSmall ? 11 : 13,
              fill: "#222",
              fontWeight: 600,
            }}
            axisLine={{ stroke: "#999", strokeWidth: 1.2 }}
            tickLine={false}
            angle={isSmall ? -25 : 0}
            textAnchor={isSmall ? "end" : "middle"}
          />

          {/* BRUSH */}
          <Brush
            dataKey="Subject"
            height={10}
            travellerWidth={8}
            stroke="#94a3b8"
            endIndex={Math.min(data.length - 1, 4)}
          />

          {/* Y AXIS */}
          <YAxis
            tick={{
              fontSize: 12,
              fill: "#222",
              fontWeight: 600,
            }}
            axisLine={{ stroke: "#999", strokeWidth: 1.2 }}
            tickLine={false}
            width={40}
            label={{
              value: "Score",
              angle: -90,
              position: "insideLeft",
              style: {
                textAnchor: "middle",
                fontSize: 12,
                fontWeight: 700,
                fill: "#111",
              },
            }}
          />

          {/* TOOLTIP */}
          <Tooltip
            contentStyle={{
              borderRadius: "10px",
              border: "none",
              boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
              fontSize: "13px",
            }}
          />

          {/* BARS */}
          <Bar
            dataKey="Strength"
            stackId="a"
            fill="#06b6d4"
            maxBarSize={45}
          >
            <LabelList content={renderSmartLabel(data, "Strength", isSmall)} />
          </Bar>

          <Bar
            dataKey="Gap"
            stackId="a"
            fill="#ef4444"
            maxBarSize={45}
          >
            <LabelList content={renderSmartLabel(data, "Gap", isSmall)} />
          </Bar>

          {/* LEGEND */}
          <Legend
            wrapperStyle={{
              fontSize: "14px",
              fontWeight: 600,
              paddingTop: 10,
              height:"0px"
            }}
          />
        </BarChart>
      </ResponsiveContainer>
    </ChartWrapper>
  );
}